<template>
  <aside v-if="headings.length > 1" class="toc">
    <h3 class="toc-title">On this page</h3>
    <ul class="toc-list">
      <li
        v-for="h in headings"
        :key="h.id"
        class="toc-item"
        :class="`toc-item--h${h.level}`"
      >
        <a
          :href="`#${h.id}`"
          class="toc-link"
          :class="{ 'toc-link--active': activeId === h.id }"
          @click.prevent="scrollTo(h.id)"
        >
          {{ h.text }}
        </a>
      </li>
    </ul>
  </aside>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

interface TocHeading {
  id: string
  text: string
  level: number
}

const props = defineProps<{
  contentSelector: string
}>()

const headings = ref<TocHeading[]>([])
const activeId = ref('')
let observer: IntersectionObserver | null = null

const generateId = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

const buildToc = () => {
  const container = document.querySelector(props.contentSelector)
  if (!container) return

  const els = container.querySelectorAll('h1, h2, h3')
  const items: TocHeading[] = []

  els.forEach((el) => {
    const htmlEl = el as HTMLElement
    if (!htmlEl.id) {
      htmlEl.id = generateId(htmlEl.textContent || '')
    }
    items.push({
      id: htmlEl.id,
      text: htmlEl.textContent || '',
      level: parseInt(htmlEl.tagName[1], 10),
    })
  })

  headings.value = items

  // Intersection Observer for scroll-spy
  if (observer) observer.disconnect()

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          activeId.value = entry.target.id
        }
      }
    },
    { rootMargin: '-80px 0px -60% 0px' }
  )

  els.forEach((el) => observer?.observe(el))
}

const scrollTo = (id: string) => {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    activeId.value = id
  }
}

onMounted(() => {
  // Wait for content to render
  setTimeout(buildToc, 100)
})

onUnmounted(() => {
  observer?.disconnect()
})

defineExpose({ buildToc })
</script>

<style scoped>
.toc {
  width: var(--toc-width);
  flex-shrink: 0;
  padding: var(--space-lg) var(--space-md);
  position: sticky;
  top: var(--header-height);
  max-height: calc(100vh - var(--header-height));
  overflow-y: auto;
}

.toc-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
  margin: 0 0 var(--space-md);
}

.toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.toc-item {
  margin: 2px 0;
}

.toc-item--h2 {
  padding-left: var(--space-md);
}

.toc-item--h3 {
  padding-left: var(--space-xl);
}

.toc-link {
  display: block;
  padding: 4px var(--space-sm);
  font-size: var(--font-size-sm);
  line-height: 1.4;
  color: var(--color-text-muted);
  text-decoration: none;
  border-radius: var(--radius-sm);
  border-left: 2px solid transparent;
  transition: color var(--transition-fast), border-color var(--transition-fast), background var(--transition-fast);
}

.toc-link:hover {
  color: var(--color-text);
  background: var(--color-surface-hover);
}

.toc-link--active {
  color: var(--color-primary);
  border-left-color: var(--color-primary);
  font-weight: 500;
}
</style>
